import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { Installment } from "@/types/installment";
import { formatDueDate, formatINR } from "../data";

const REASONS = ["Customer Request", "Payment Received in Cash", "Product Returned", "Dispute", "Other"].map((r) => ({
  value: r,
  label: r,
}));

interface SkipModalProps {
  open: boolean;
  onClose: () => void;
  installment: Installment | null;
  onConfirm: (reason: string, remarks: string) => void;
  loading?: boolean;
}

export default function SkipModal({ open, onClose, installment, onConfirm, loading }: SkipModalProps) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");

  function handleClose() {
    if (loading) return;
    setReason("");
    setRemarks("");
    setError("");
    onClose();
  }

  function handleConfirm() {
    if (!reason) {
      setError("Select a reason for skipping this installment");
      return;
    }
    onConfirm(reason, remarks.trim());
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Skip this installment?"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Keep Installment
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={loading}>
            Skip Installment
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3.5">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <p className="text-[13px] text-ink">
          This installment will not be presented to the bank. It cannot be un-skipped, and the schedule of the
          remaining installments does not change.
        </p>
      </div>

      <div className="mt-4 rounded-lg border border-line bg-page p-4">
        <p className="text-sm font-semibold text-ink">Installment</p>
        <div className="mt-2 space-y-2.5">
          {[
            {
              label: "Installment Number",
              value: installment ? `${installment.installmentNumber} of ${installment.totalInstallments}` : "—",
            },
            { label: "Amount", value: installment ? formatINR(installment.amount) : "—" },
            { label: "Due Date", value: installment ? formatDueDate(installment.dueDate) : "—" },
          ].map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-4">
              <span className="text-[13px] text-ink-secondary">{r.label}</span>
              <span className="text-right text-sm font-medium text-ink">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Select
          label="Reason"
          required
          options={REASONS}
          placeholder="Select reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value) setError("");
          }}
          error={error}
        />
      </div>
      <div className="mt-4">
        <Textarea
          label="Remarks"
          required
          rows={3}
          maxLength={200}
          placeholder="Explain why this installment is being skipped"
          helper={`${remarks.length}/200 characters`}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>
    </Modal>
  );
}